import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { connect } from 'react-redux';
import { keyframes } from 'tss-react';
import { makeStyles } from 'tss-react/mui';
import { leaveConference } from '../../base/conference/actions';
import { getRoomName } from '../../base/conference/functions';
import { setAudioInputDeviceAndUpdateSettings, setVideoInputDeviceAndUpdateSettings } from '../../base/devices/actions.web';
import i18next from '../../base/i18n/i18next';
import VideoTrack from '../../base/media/components/web/VideoTrack';
import { MEDIA_TYPE } from '../../base/media/constants';
import { getLocalParticipant } from '../../base/participants/functions';
import { updateSettings } from '../../base/settings/actions';
import { getCurrentCameraDeviceId, getCurrentMicDeviceId, getUserSelectedCameraDeviceId } from '../../base/settings/functions.web';
import { getVideoTrackByParticipant, isLocalTrackMuted } from '../../base/tracks/functions.web';
import { down } from '../../base/ui/breakpoints';
import { areDebugOverlaysEnabled, isInterpreter } from '../../base/user-role/functions';
import { registerDesktopCall } from '../../base/util/desktopWindow';
import { isUnifiedRoomModel, isVriApp } from '../../base/whitelabel/functions';
import CallWaitingOverlay from '../../call-management/components/CallWaitingOverlay';
import GroupJoinRequestBanner from '../../call-management/components/GroupJoinRequestBanner.web';
import HeldCallBar from '../../call-management/components/HeldCallBar.web';
import IncomingCallOverlay from '../../call-management/components/IncomingCallOverlay';
import { otherCallHint, otherCallSpoken, otherCallText, parseOtherCallStatus } from '../../call-management/otherCall';
import { emitMobileTelemetryEvent } from '../../mobile/navigation/telemetry';
import { handleToggleVideoMuted } from '../../toolbox/actions.any';
import { isToolboxVisible, isVideoMuteButtonDisabled } from '../../toolbox/functions.web';
import { shouldDisplayTileView } from '../../video-layout/functions.web';
import { getLocalVisualLanguageRole, getVisualLanguageMediaEvidence, inferRemoteRoles } from '../../visual-language-media-policy/functions';
import { inferPlatformRoomRoles } from '../../visual-language-media-policy/roles';
import { getPersistentItem, hasPersistentAuthToken, setPersistentItem } from '../../vrs-auth/storage';
import { callNamesAlwaysOn } from '../callNames';
import { desktopMiniPaneKey } from '../desktopMini';
import { resolvePeerVideo, resolvePeerWaiting } from '../peerWaiting';
import { crossesSlot, isPictureFitted, toggleFitChoice } from '../pictureFit';
import { createRemoteVideoDiagnostics } from '../remoteVideoDiagnostics';
import { getRoomPaneKind } from '../roomPanes';
import { selfViewDocksInGrid } from '../stageGrid';
import { readStoredActiveCall } from '../storedCall';
import { NO_TILE_ORDER, arrangePanes, swapTiles, tileOrderKeys } from '../tileOrder';
import { useRemoteVideoStallGuard } from '../useRemoteVideoStallGuard.web';
import { fitToSlot } from '../videoShape';
import RingClientBanner from './RingClientBanner.web';
import useSelfViewPosition from './useSelfViewPosition.web';
import AudioAutoplayPrompt from './web/AudioAutoplayPrompt.web';
import CallTopBar from './web/CallTopBar.web';
import PeerWaitingPane, { PEER_PANE_FADE_MS } from './web/PeerWaitingPane.web';
import PictureFitButton from './web/PictureFitButton.web';
import SelfViewDeviceSettings, { SELF_VIEW_CONTROL_SIZE } from './web/SelfViewDeviceSettings.web';
import TileSwapMenu from './web/TileSwapMenu.web';
import useNameLifts from './web/useNameLifts.web';
import usePictureShapes from './web/usePictureShapes.web';
import useTileDrag from './web/useTileDrag.web';
import { pruneReadyTracks, shouldShowHeldFrame } from '../heldFrame';
import { getHeldFrame, getHeldFramesVersion, subscribeHeldFrames } from '../heldFrames.web';
/**
 * Translate a key to a plain string.
 *
 * `i18next.t` is typed to return `string | object | array | null`, which JSX
 * children and our `string` pane fields both reject. Every key used here is a
 * leaf string, so narrowing once at the boundary keeps the call sites clean.
 */
function tr(key, options) {
    return String(i18next.t(key, options ?? {}));
}
// Every picture fills its part of the stage (cover) by default, as on native
// (Robert, 2026-10-05: "all video should fill their respective portions of
// the screen (unless they zoom out)"). A fitted remote picture is a rounded
// card of its own shape, where cover is the whole picture. Only while its
// shape is not known does it show whole between dark bars (contain). Module
// constants keep the style identity stable for Video's shouldComponentUpdate.
const FILL_VIDEO_STYLE = {
    height: '100%',
    objectFit: 'cover',
    width: '100%'
};
const FIT_VIDEO_STYLE = {
    height: '100%',
    objectFit: 'contain',
    width: '100%'
};
// The phone camera-off self-view card. It holds the 44 px corner controls
// and a 4 px gap, one 13 px title line, a 4 px gap, two 11 px state lines
// (line height 1.25) and 6 px of padding: about 102 px in all.
const NARROW_SELF_VIEW_WIDTH = 160;
const NARROW_SELF_VIEW_MIN_HEIGHT = 104;
const useStyles = makeStyles()(theme => ({
    root: {
        background: '#040404',
        boxSizing: 'border-box',
        display: 'grid',
        gridAutoRows: 'minmax(0, 1fr)',
        height: '100%',
        minHeight: 0,
        position: 'relative',
        width: '100%'
    },
    singlePaneRoot: {
        gridTemplateColumns: 'minmax(0, 1fr)'
    },
    twoPaneRoot: {
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        '@media (orientation: portrait)': {
            gridTemplateColumns: '1fr'
        }
    },
    threePaneRoot: {
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        '@media (orientation: portrait)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }
    },
    // Just the video, rounded — same treatment as the native tiles: no card
    // border, blur, or shadow around each person.
    pane: {
        background: '#0b0f16',
        borderRadius: 14,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
        position: 'relative'
    },
    // A fitted picture (pictureFit.ts): a rounded card of the picture's own
    // shape in the middle of its pane, on the stage background, not a
    // picture between dark bars. Its name and fit button sit on the card.
    paneShaped: {
        alignItems: 'center',
        background: 'transparent',
        justifyContent: 'center'
    },
    // The device popover may extend past a small self-view tile.
    paneDeviceSettingsOpen: {
        overflow: 'visible'
    },
    // The tile of another person moves with a drag (useTileDrag.web.ts). A
    // touch on it neither scrolls nor selects, and a long press opens no
    // callout.
    paneSwappable: {
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        touchAction: 'none',
        userSelect: 'none'
    },
    // The lifted tile, while its outline follows the pointer.
    paneLifted: {
        opacity: 0.5,
        transition: 'opacity 150ms ease',
        '@media (prefers-reduced-motion: reduce)': {
            transition: 'none'
        }
    },
    // The tile that takes the drop.
    paneDropTarget: {
        outline: `3px solid ${theme.palette.focus01}`,
        outlineOffset: -3
    },
    // The outline of the lifted tile under the pointer. useTileDrag.web.ts
    // sets its size and moves it.
    tileGhost: {
        alignItems: 'center',
        background: 'rgba(4, 8, 14, 0.55)',
        border: '2px solid rgba(245, 248, 251, 0.85)',
        borderRadius: 14,
        boxSizing: 'border-box',
        color: '#F8FBFF',
        display: 'flex',
        justifyContent: 'center',
        left: 0,
        overflow: 'hidden',
        padding: 8,
        pointerEvents: 'none',
        position: 'fixed',
        top: 0,
        zIndex: 20
    },
    tileGhostName: {
        fontSize: 15,
        fontWeight: 600,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    // VRS stage: remote participants own the grid; the local tile floats as
    // a picture-in-picture so the person you watch (the signer for the terp,
    // the terp for the Deaf client) gets the screen.
    vrsFocusRootSingle: {
        gridTemplateColumns: 'minmax(0, 1fr)'
    },
    vrsFocusRootDual: {
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        '@media (orientation: portrait)': {
            gridTemplateColumns: '1fr'
        }
    },
    vrsSelfPip: {
        border: `1px solid ${theme.palette.ui04}`,
        aspectRatio: '16 / 10',
        maxHeight: 'calc(100% - 152px)',
        position: 'absolute',
        width: 'clamp(120px, 30%, 260px)',
        maxWidth: '40%',
        zIndex: 6
    },
    // Only the phone camera-off card grows, so its name and state fit below
    // the corner controls; with the camera on, the inset keeps its size and
    // the remote video stays dominant. The 40% cap still applies, and the
    // card is never shorter than its content, even on a short stage.
    vrsSelfPipEmpty: {
        [`@media ${down('xs')}`]: {
            aspectRatio: '4 / 3',
            minHeight: NARROW_SELF_VIEW_MIN_HEIGHT,
            width: NARROW_SELF_VIEW_WIDTH
        }
    },
    moveSelfView: {
        position: 'absolute', top: 0, right: 0, zIndex: 7,
        minHeight: SELF_VIEW_CONTROL_SIZE,
        minWidth: SELF_VIEW_CONTROL_SIZE,
        border: 0, borderRadius: theme.shape.borderRadius,
        background: theme.palette.ui01, color: theme.palette.text01,
        cursor: 'move', touchAction: 'none', fontSize: 22,
        '&:focus-visible': { outline: `2px solid ${theme.palette.focus01}`, outlineOffset: -2 }
    },
    paneHeader: {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        padding: 4
    },
    paneTitleBlock: {
        minWidth: 0
    },
    paneTitle: {
        color: '#F5F8FB',
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase'
    },
    paneDescription: {
        color: 'rgba(226, 236, 247, 0.78)',
        fontSize: 13,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    statusBadge: {
        background: 'rgba(255, 255, 255, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: 999,
        color: '#EAF2FA',
        fontSize: 11,
        fontWeight: 600,
        padding: 4
    },
    mediaFrame: {
        background: '#050505',
        borderRadius: 18,
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        position: 'relative'
    },
    video: {
        height: '100%',
        objectFit: 'cover',
        // The peer's first frame fades in as the waiting splash fades out.
        transition: `opacity ${PEER_PANE_FADE_MS}ms ease`,
        width: '100%',
        '@media (prefers-reduced-motion: reduce)': {
            transition: 'none'
        }
    },
    // Letterbox bars around a whole remote picture stay stage-dark.
    remoteVideo: {
        background: theme.palette.uiBackground
    },
    videoPending: {
        opacity: 0
    },
    // The picture is not live (the bridge paused it, or it froze while the
    // monitor recovers it): dimmed, never passed off as a live frame.
    videoPaused: {
        filter: 'brightness(0.45) saturate(0.5)'
    },
    // Another call on this person's phone: one steady sentence, not a picture
    // that comes and goes (call-management/otherCall.ts).
    otherCallVeil: {
        alignItems: 'center',
        background: 'rgba(0, 0, 0, 0.82)',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        fontSize: 20,
        fontWeight: 600,
        inset: 0,
        justifyContent: 'center',
        padding: '0 24px',
        pointerEvents: 'none',
        position: 'absolute',
        textAlign: 'center',
        zIndex: 3
    },
    // "Hang tight" under the sentence (Robert, 2026-11-02): the same colour,
    // lighter. Three dots pulse slowly; with reduced motion they stand still.
    otherCallHint: {
        fontSize: 16,
        fontWeight: 400,
        marginTop: theme.spacing(1),
        opacity: 0.75
    },
    otherCallDot: {
        animation: `${keyframes `
            0%, 60%, 100% { opacity: 0.33; }
            30% { opacity: 1; }
        `} 1.4s ease-in-out infinite`,
        marginLeft: 1,
        opacity: 0.33,
        '&:nth-of-type(2)': {
            animationDelay: '0.2s'
        },
        '&:nth-of-type(3)': {
            animationDelay: '0.4s'
        },
        '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
            opacity: 0.66
        }
    },
    // Said once, in full, by a screen reader; not seen.
    otherCallSpoken: {
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        position: 'absolute',
        whiteSpace: 'nowrap',
        width: 1
    },
    pausedMark: {
        alignItems: 'center',
        background: 'rgba(0, 0, 0, 0.55)',
        borderRadius: '50%',
        display: 'flex',
        gap: 6,
        height: 44,
        justifyContent: 'center',
        left: '50%',
        pointerEvents: 'none',
        position: 'absolute',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: 44,
        '&::before, &::after': {
            background: '#fff',
            borderRadius: 2,
            content: '""',
            height: 16,
            width: 5
        }
    },
    // Someone's last frame while their call moves between the direct
    // connection and the bridge (heldFrames.web.ts): over the waiting video
    // element, fading out as the new video fades in.
    heldFrame: {
        background: theme.palette.uiBackground,
        inset: 0,
        opacity: 1,
        pointerEvents: 'none',
        position: 'absolute',
        transition: `opacity ${PEER_PANE_FADE_MS}ms ease`,
        '@media (prefers-reduced-motion: reduce)': {
            transition: 'none'
        }
    },
    heldFrameFading: {
        opacity: 0
    },
    deviceRow: {
        display: 'grid',
        gap: 3
    },
    deviceLabel: {
        color: 'rgba(236, 244, 252, 0.76)',
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: '0.08em',
        textTransform: 'uppercase'
    },
    deviceSelect: {
        background: 'rgba(255, 255, 255, 0.94)',
        border: 0,
        borderRadius: 8,
        color: '#101827',
        font: 'inherit',
        fontSize: 13,
        minHeight: 44,
        minWidth: 0,
        padding: '6px 7px',
        width: '100%',
        '& option': {
            background: '#FFFFFF',
            color: '#101827'
        }
    },
    deviceAction: {
        background: 'rgba(255, 255, 255, 0.94)',
        border: 0,
        borderRadius: 8,
        color: '#101827',
        cursor: 'pointer',
        font: 'inherit',
        fontSize: 13,
        fontWeight: 700,
        minHeight: 44,
        padding: '6px 7px',
        width: '100%',
        '&:disabled': {
            cursor: 'not-allowed',
            opacity: 0.55
        }
    },
    emptyState: {
        alignItems: 'center',
        color: '#F3F6F9',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'center',
        padding: theme.spacing(3),
        textAlign: 'center'
    },
    emptyBadge: {
        alignItems: 'center',
        background: 'rgba(255, 255, 255, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        borderRadius: '50%',
        display: 'flex',
        fontSize: 24,
        fontWeight: 700,
        height: 68,
        justifyContent: 'center',
        width: 68
    },
    emptyTitle: {
        fontSize: 18,
        fontWeight: 700
    },
    emptyCopy: {
        color: 'rgba(226, 236, 247, 0.78)',
        fontSize: 14,
        lineHeight: 1.5,
        maxWidth: 280
    },
    // A phone self-view has two 44 px corner controls. Keep the camera-off
    // identity and state below that band; full-size and docked panes retain
    // their ordinary centered empty state. Pixel values: the theme's spacing
    // is an index scale, so fractional steps resolve to nothing.
    vrsSelfPipEmptyState: {
        [`@media ${down('xs')}`]: {
            alignItems: 'stretch',
            boxSizing: 'border-box',
            gap: 4,
            justifyContent: 'flex-end',
            padding: `${SELF_VIEW_CONTROL_SIZE + 4}px 6px 6px`
        }
    },
    vrsSelfPipEmptyBadge: {
        [`@media ${down('xs')}`]: {
            display: 'none'
        }
    },
    vrsSelfPipEmptyTitle: {
        [`@media ${down('xs')}`]: {
            fontSize: 13,
            lineHeight: 1.25,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '100%'
        }
    },
    vrsSelfPipEmptyCopy: {
        [`@media ${down('xs')}`]: {
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            display: '-webkit-box',
            fontSize: 11,
            lineHeight: 1.25,
            maxWidth: 'none',
            overflow: 'hidden',
            overflowWrap: 'anywhere',
            width: '100%'
        }
    },
    participantLabel: {
        backdropFilter: 'blur(6px)',
        background: 'rgba(10, 16, 28, 0.55)',
        borderRadius: 10,
        bottom: 12,
        boxSizing: 'border-box',
        color: '#F8FBFF',
        left: 12,
        maxWidth: 'calc(100% - 24px)',
        overflow: 'hidden',
        padding: '5px 10px',
        position: 'absolute',
        transition: 'opacity 220ms ease, transform 220ms ease'
    },
    // Only with "Always show names in calls" off (callNames.ts): the name
    // then rides on the toolbar, visible after a click or mouse move.
    participantLabelHidden: {
        opacity: 0
    },
    participantName: {
        fontSize: 15,
        fontWeight: 600,
        letterSpacing: '0.015em',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    participantMeta: {
        color: 'rgba(233, 241, 247, 0.82)',
        fontSize: 12,
        marginTop: 3
    },
    mediaEvidence: {
        background: 'rgba(5, 10, 18, 0.86)',
        border: '1px solid rgba(162, 213, 255, 0.32)',
        borderRadius: 8,
        bottom: 12,
        boxShadow: '0 14px 32px rgba(0, 0, 0, 0.28)',
        boxSizing: 'border-box',
        color: '#EAF4FF',
        fontSize: 12,
        left: 12,
        maxWidth: 'min(430px, calc(100% - 24px))',
        padding: '7px 9px',
        position: 'absolute',
        zIndex: 6,
        [theme.breakpoints.down('sm')]: {
            bottom: theme.spacing(1),
            left: theme.spacing(1),
            right: theme.spacing(1)
        }
    },
    mediaEvidenceSummary: {
        cursor: 'pointer',
        fontWeight: 800,
        outline: 0
    },
    mediaEvidenceGrid: {
        display: 'grid',
        gap: 3,
        gridTemplateColumns: 'auto minmax(0, 1fr)',
        marginTop: 6
    },
    mediaEvidenceLabel: {
        color: 'rgba(209, 229, 247, 0.72)',
        fontWeight: 700
    },
    mediaEvidenceValue: {
        color: '#FFFFFF',
        minWidth: 0
    }
}));
function getDeviceLabel(device, fallback, index) {
    return device.label || `${fallback} ${index + 1}`;
}
function persistDevicePreference(kind, deviceId) {
    setPersistentItem(kind === 'camera' ? 'vrs_camera_device_id' : 'vrs_microphone_device_id', deviceId);
}
function persistDeviceLabel(kind, label) {
    if (!label) {
        return;
    }
    setPersistentItem(kind === 'camera' ? 'vrs_camera_device_label' : 'vrs_microphone_device_label', label);
}
function getDevicePreference(kind) {
    const key = kind === 'camera' ? 'vrs_camera_device_id' : 'vrs_microphone_device_id';
    return getPersistentItem(key) || '';
}
function getDeviceLabelPreference(kind) {
    const key = kind === 'camera' ? 'vrs_camera_device_label' : 'vrs_microphone_device_label';
    return getPersistentItem(key) || '';
}
function normalizeCameraLabel(label) {
    return label.replace(/\s#\d*(?!.*\s#\d*)/, '').trim();
}
function findMatchingCameraByLabel(devices, label) {
    const normalizedLabel = normalizeCameraLabel(label);
    return normalizedLabel
        ? devices.find(device => normalizeCameraLabel(device.label || '') === normalizedLabel)
        : undefined;
}
function findPreferredCamera(devices, userSelectedCameraDeviceId) {
    if (userSelectedCameraDeviceId && devices.some(device => device.deviceId === userSelectedCameraDeviceId)) {
        return userSelectedCameraDeviceId;
    }
    const storedCameraId = getDevicePreference('camera');
    if (storedCameraId && devices.some(device => device.deviceId === storedCameraId)) {
        return storedCameraId;
    }
    const storedCameraLabel = getDeviceLabelPreference('camera');
    const labelMatch = findMatchingCameraByLabel(devices, storedCameraLabel);
    if (labelMatch?.deviceId) {
        return labelMatch.deviceId;
    }
    return '';
}
function isVriHostname() {
    if (typeof window === 'undefined') {
        return false;
    }
    const hostname = window.location.hostname.toLowerCase();
    return hostname.startsWith('vri.')
        || hostname.startsWith('staging-vri.')
        || hostname.includes('maplecomm.ca');
}
export function isVriConferenceContext(roomName) {
    return isVriApp()
        || isVriHostname()
        || Boolean(roomName?.startsWith('vri-') || roomName?.startsWith('instant-'));
}
function getStoredVrsRole() {
    if (typeof window !== 'undefined') {
        const role = new URLSearchParams(window.location.search).get('role');
        if (role === 'client' || role === 'interpreter') {
            return role;
        }
    }
    const role = getPersistentItem('vrs_user_role');
    if (role === 'client' || role === 'interpreter') {
        return role;
    }
    if (isVriApp()) {
        return isInterpreter() ? 'interpreter' : 'client';
    }
    return undefined;
}
function getStoredTargetClient() {
    const value = getPersistentItem('vrs_target_client');
    return value || undefined;
}
function getParticipantName(participant, fallback = 'Waiting to Join') {
    return participant?.name || participant?.displayName || fallback;
}
function isVisibleRoomParticipant(participant) {
    return !participant.fakeParticipant
        && !participant.isJigasi
        && !participant.botType;
}
function isRenderableVideoTrack(videoTrack) {
    if (!videoTrack) {
        return false;
    }
    const jitsiTrackMuted = typeof videoTrack.jitsiTrack?.isMuted === 'function'
        ? videoTrack.jitsiTrack.isMuted()
        : false;
    return !videoTrack.muted
        && !jitsiTrackMuted
        && videoTrack.isReceivingData !== false;
}
function getPaneStatusText(participant, videoTrack, awaitingText) {
    if (!participant) {
        return awaitingText;
    }
    return isRenderableVideoTrack(videoTrack) ? 'Live' : 'Camera off';
}
function withoutParticipant(participants, participant) {
    if (!participant) {
        return participants;
    }
    return participants.filter(({ id }) => id !== participant.id);
}
export function isVrsSession(roomName) {
    if (getStoredVrsRole()) {
        return true;
    }
    if (getStoredTargetClient()) {
        return true;
    }
    return Boolean(roomName?.startsWith('vrs-'));
}
function getPaneEmptyMessage(role, hasParticipant) {
    if (hasParticipant) {
        return tr('vrs.layout.joinedNoCamera');
    }
    if (role === 'extra') {
        return tr('vrs.layout.extraEmpty');
    }
    if (role === 'interpreter') {
        return tr('vrs.layout.interpreterEmpty');
    }
    if (role === 'client') {
        return tr('vrs.layout.clientEmpty');
    }
    return tr(isUnifiedRoomModel() ? 'vrs.layout.participantEmpty' : 'vrs.layout.hearingEmpty');
}
function getTrackIdentity(videoTrack) {
    return videoTrack?.jitsiTrack || videoTrack;
}
/**
 * Someone's held last frame (heldFrames.web.ts), drawn in its own element so
 * the pane's video element is left to the new track.
 *
 * @param {Object} props - The frame's canvas and the element's class.
 * @returns {ReactElement}
 */
function HeldFrame({ canvas, className }) {
    const host = useRef(null);
    useLayoutEffect(() => {
        const element = host.current;
        if (!element) {
            return undefined;
        }
        canvas.style.cssText = 'display:block;height:100%;object-fit:contain;width:100%';
        element.replaceChildren(canvas);
        return () => {
            if (canvas.parentNode === element) {
                element.removeChild(canvas);
            }
        };
    }, [canvas]);
    return (React.createElement("div", { "aria-hidden": 'true', className: className, ref: host }));
}
/**
 * The partner's camera for their pane's stage line (peerWaiting.ts
 * resolvePeerVideo): whether their first frame has played, their camera
 * track and its mute, and the camera sources their endpoint signalled. A
 * partner who joined with the camera off has no track at all, so without a
 * track or a source nothing is claimed about their video.
 */
function getPeerVideo(pane, rendered) {
    if (!pane.participant) {
        return 'none';
    }
    const track = pane.videoTrack;
    const jitsiMuted = typeof track?.jitsiTrack?.isMuted === 'function' ? track.jitsiTrack.isMuted() : false;
    return resolvePeerVideo({
        cameraSources: pane.participant.sources?.get(MEDIA_TYPE.VIDEO)?.values(),
        rendered,
        track: track ? { muted: Boolean(track.muted || jitsiMuted) } : undefined
    });
}
const VRSLayout = ({ _hasAccountContext, _isObserver, _localVrsRole, _audioInputDevices, _toolboxVisible, _currentCameraDeviceId, _currentMicDeviceId, _hasLocalVideoTrack, _mediaEvidence, _panes, _roomName, _tileViewEnabled, _userSelectedCameraDeviceId, _videoMuteDisabled, _videoMuted, _videoInputDevices, dispatch }) => {
    const { classes, cx } = useStyles();
    const [readyTracks, setReadyTracks] = useState(() => new Set());
    // Re-render when a last frame is held or let go (a move between the
    // direct connection and the bridge).
    useSyncExternalStore(subscribeHeldFrames, getHeldFramesVersion);
    // A track that left the panes is forgotten: when a call moves back to the
    // bridge the same track objects return with no frame flowing yet.
    useEffect(() => {
        setReadyTracks(current => pruneReadyTracks(current, _panes.map(pane => getTrackIdentity(pane.videoTrack)).filter(Boolean)));
    }, [_panes]);
    const [deviceSettingsOpen, setDeviceSettingsOpen] = useState(false);
    // The remote people whose fit button the viewer tapped (pictureFit.ts).
    // In memory for this call only: not stored and not sent to anyone.
    const [fitChoice, setFitChoice] = useState({});
    // The order the viewer gave the other people's tiles (tileOrder.ts). In
    // memory for this call only: not stored and not sent to anyone.
    const [tileOrder, setTileOrder] = useState(NO_TILE_ORDER);
    const roomKey = _roomName ?? '';
    const remoteVideoDiagnostics = useMemo(() => createRemoteVideoDiagnostics(observation => {
        const activeCall = readStoredActiveCall();
        const belongsToRoom = Boolean(_roomName && activeCall?.roomName === _roomName);
        emitMobileTelemetryEvent('vrs_remote_video_state', { ...observation }, {
            callId: belongsToRoom ? activeCall?.callId : undefined,
            requestId: belongsToRoom ? activeCall?.requestId : undefined,
            roomName: _roomName,
            state: observation.phase
        });
    }), [_roomName]);
    // Diagnostic chrome (pane headers, Live badges, media health) is a debug
    // tool: hidden unless the account has debugOverlays, or an admin adds
    // ?vrsDebug=1.
    const debugOverlays = _hasAccountContext && areDebugOverlaysEnabled();
    const visiblePanes = useMemo(() => _panes.filter(pane => {
        if (!pane.participant) {
            // The call partner's pane is held open while they are on the
            // way, so the self-view is laid out where it will stay.
            return Boolean(pane.peer);
        }
        // Occupied panes stay visible even without a renderable video track:
        // a camera-off joiner (or phone-only hearing party) used to vanish,
        // so nobody could tell they were connected at all.
        return true;
    }), [_roomName, _panes]);
    const localVriPane = visiblePanes.find(pane => pane.participant?.local);
    const remotePanes = visiblePanes.filter(pane => !pane.participant?.local);
    const paneKeyOf = (pane) => {
        if (pane.peer) {
            return `peer-${pane.role}`;
        }
        return pane.participant?.id || pane.role;
    };
    const isPaneVideoReady = (pane) => {
        const trackIdentity = getTrackIdentity(pane.videoTrack);
        return Boolean(pane.videoTrack?.videoStarted || readyTracks.has(trackIdentity));
    };
    // Remote pictures that stop moving are recovered (play, fresh element,
    // keyframe) and shown as paused meanwhile; so is one the bridge stopped
    // forwarding. Screen shares may legitimately stand still.
    const stallGuard = useRemoteVideoStallGuard(_roomName, remotePanes.map(pane => ({
        paneKey: paneKeyOf(pane),
        paneRole: pane.role,
        videoTrack: pane.videoTrack,
        watch: isRenderableVideoTrack(pane.videoTrack)
            && pane.videoTrack?.videoType !== 'desktop'
            && isPaneVideoReady(pane)
    })));
    // The toolbar and custom stage share the same selector: equal tiles when
    // enabled; otherwise remote video with a movable self-view inset.
    // In a large group call the self-view takes the last grid cell instead
    // of floating over a signer (stageGrid.ts).
    const selfViewDocked = !_tileViewEnabled && Boolean(localVriPane) && selfViewDocksInGrid(remotePanes.length);
    const vrsPipMode = !_tileViewEnabled && Boolean(localVriPane) && remotePanes.length >= 1 && !selfViewDocked;
    const orderedPanes = (vrsPipMode || selfViewDocked) && localVriPane
        ? [...remotePanes, localVriPane] : visiblePanes;
    // Robert, 2026-10-07: "all tiles can switch with any other tile just by
    // dragging and dropping". The other people's tiles take the order the
    // viewer gave them in this call. Your own video and a pane that waits for
    // someone keep their places (tileOrder.ts).
    const isSwappable = (pane) => Boolean(pane.participant && !pane.participant.local);
    const stagePanes = arrangePanes(orderedPanes, paneKeyOf, isSwappable, tileOrderKeys(tileOrder, roomKey));
    const swappableKeys = stagePanes.filter(isSwappable).map(paneKeyOf);
    const swapOffered = swappableKeys.length >= 2;
    const swapPanes = (from, to, method) => {
        if (from === to || !swappableKeys.includes(from) || !swappableKeys.includes(to)) {
            return;
        }
        setTileOrder({
            keys: swapTiles(swappableKeys, from, to),
            room: roomKey
        });
        emitMobileTelemetryEvent('vrs_stage_tile_swap', {
            method,
            tiles: swappableKeys.length
        }, { roomName: _roomName });
    };
    const swapBy = (method) => (from, to) => swapPanes(from, to, method);
    // A call of two: the other person covers the whole stage, and your own
    // video floats over it.
    const callOfTwo = vrsPipMode && remotePanes.length === 1;
    const selfView = useSelfViewPosition({
        floating: vrsPipMode,
        toolboxVisible: _toolboxVisible
    });
    const tileDrag = useTileDrag({
        enabled: swapOffered,
        keys: swappableKeys,
        onSwap: swapBy('drag'),
        stage: selfView.stage
    });
    const draggedPane = tileDrag.drag && stagePanes.find(pane => paneKeyOf(pane) === tileDrag.drag?.key);
    // Names stay on while the call bar is hidden unless Settings says
    // otherwise (callNames.ts). While the bar shows, a name it would cover
    // sits just above it. A swap changes the places: measure again.
    const [namesAlwaysOn] = useState(callNamesAlwaysOn);
    const stageLayoutKey = `${_tileViewEnabled}|${stagePanes.map(paneKeyOf).join('|')}`;
    const nameLifts = useNameLifts(selfView.stage, _toolboxVisible, stageLayoutKey);
    const pictureShapes = usePictureShapes(selfView.stage, stageLayoutKey);
    const cameraSelectValue = _videoInputDevices.some(device => device.deviceId === _currentCameraDeviceId)
        ? _currentCameraDeviceId
        : '';
    const micSelectValue = _audioInputDevices.some(device => device.deviceId === _currentMicDeviceId)
        ? _currentMicDeviceId
        : '';
    const onCameraChange = (event) => {
        const deviceId = event.target.value;
        const deviceLabel = _videoInputDevices.find(device => device.deviceId === deviceId)?.label;
        persistDevicePreference('camera', deviceId);
        persistDeviceLabel('camera', deviceLabel);
        if (_hasLocalVideoTrack) {
            dispatch(setVideoInputDeviceAndUpdateSettings(deviceId));
        }
        else {
            dispatch(updateSettings({
                userSelectedCameraDeviceId: deviceId,
                userSelectedCameraDeviceLabel: deviceLabel
            }));
        }
    };
    const onMicChange = (event) => {
        const deviceId = event.target.value;
        const deviceLabel = _audioInputDevices.find(device => device.deviceId === deviceId)?.label;
        persistDevicePreference('microphone', deviceId);
        persistDeviceLabel('microphone', deviceLabel);
        dispatch(setAudioInputDeviceAndUpdateSettings(deviceId));
    };
    const onToggleCamera = () => {
        dispatch(handleToggleVideoMuted(!_videoMuted, true, true));
    };
    useEffect(() => {
        if (_isObserver) {
            return;
        }
        const preferredCameraId = findPreferredCamera(_videoInputDevices, _userSelectedCameraDeviceId);
        if (preferredCameraId && preferredCameraId !== _currentCameraDeviceId && !_hasLocalVideoTrack) {
            const deviceLabel = _videoInputDevices.find(device => device.deviceId === preferredCameraId)?.label;
            persistDevicePreference('camera', preferredCameraId);
            persistDeviceLabel('camera', deviceLabel);
            dispatch(updateSettings({
                userSelectedCameraDeviceId: preferredCameraId,
                userSelectedCameraDeviceLabel: deviceLabel
            }));
        }
    }, [_isObserver, _currentCameraDeviceId, _hasLocalVideoTrack, _userSelectedCameraDeviceId, _videoInputDevices, dispatch]);
    const markTrackReady = useCallback((videoTrack) => {
        const trackIdentity = getTrackIdentity(videoTrack);
        if (!trackIdentity) {
            return;
        }
        setReadyTracks(current => {
            if (current.has(trackIdentity)) {
                return current;
            }
            const next = new Set(current);
            next.add(trackIdentity);
            return next;
        });
    }, []);
    // One handler for every fit button: the button names the person and
    // the default that its tap changes.
    const toggleFit = useCallback((participantId, fitByDefault) => {
        setFitChoice(current => toggleFitChoice(current, participantId, fitByDefault));
    }, []);
    // The MalkaVRS desktop app (desktop/malkavrs/src/mini-mode.js): this room
    // is a live video call, so closing the window makes it the mini window,
    // which shows the tile marked data-desktop-mini. Hang up there hangs up
    // as the room's own hang-up does. In a browser nothing happens.
    const desktopMiniKey = desktopMiniPaneKey(stagePanes.map(pane => ({
        hasVideo: isRenderableVideoTrack(pane.videoTrack),
        key: paneKeyOf(pane),
        local: Boolean(pane.participant?.local),
        participantId: pane.participant?.id
    })));
    useEffect(() => registerDesktopCall('video', control => {
        if (control === 'hangup') {
            dispatch(leaveConference());
        }
    }), [dispatch]);
    if (!visiblePanes.length) {
        return null;
    }
    return (React.createElement("div", { className: cx(classes.root, vrsPipMode && remotePanes.length === 1 && classes.vrsFocusRootSingle, vrsPipMode && remotePanes.length >= 2 && classes.vrsFocusRootDual, !vrsPipMode && visiblePanes.length === 1 && classes.singlePaneRoot, !vrsPipMode && visiblePanes.length === 2 && classes.twoPaneRoot, !vrsPipMode && visiblePanes.length >= 3 && classes.threePaneRoot), "data-view": _tileViewEnabled ? 'split' : 'focus', id: 'vrs-layout-root', ref: selfView.stage, style: vrsPipMode && remotePanes.length >= 2
            ? { gridTemplateColumns: `repeat(${Math.min(remotePanes.length, 3)}, minmax(0, 1fr))` }
            : undefined },
        _hasAccountContext && React.createElement(CallWaitingOverlay, null),
        _hasAccountContext && React.createElement(GroupJoinRequestBanner, null),
        _hasAccountContext && _localVrsRole !== 'interpreter' && React.createElement(HeldCallBar, null),
        _hasAccountContext && React.createElement(IncomingCallOverlay, null),
        _localVrsRole === 'client' && (React.createElement(CallTopBar, { peerName: getParticipantName(_panes.find(pane => pane.role === 'interpreter')?.participant, '') || undefined, peerPresent: remotePanes.some(pane => Boolean(pane.participant)), roomName: _roomName })),
        React.createElement(AudioAutoplayPrompt, { role: _localVrsRole || (_isObserver ? 'observer' : 'guest') }),
        _localVrsRole === 'interpreter' && !isVriConferenceContext(_roomName) && (React.createElement(RingClientBanner, { clientMissing: !_panes.some(pane => pane.role === 'client' && pane.participant) })),
        stagePanes.map(pane => {
            const participantName = getParticipantName(pane.participant, pane.title);
            const emptyMessage = getPaneEmptyMessage(pane.role, Boolean(pane.participant));
            const shouldRenderVideo = isRenderableVideoTrack(pane.videoTrack);
            const isVideoReady = isPaneVideoReady(pane);
            const paneKey = paneKeyOf(pane);
            const isRemote = !pane.participant?.local;
            const remotePaused = isRemote && isVideoReady && stallGuard.paused(paneKey);
            const shouldShowDevicePanel = pane.participant?.local
                && (pane.role === 'client' || pane.role === 'interpreter');
            const isFloatingSelfView = vrsPipMode && Boolean(pane.participant?.local);
            // The other people's tiles change places with a drag, or
            // with the swap menu from the keyboard.
            const swappable = swapOffered && isSwappable(pane);
            const swapTargets = swappable
                ? stagePanes.filter(other => isSwappable(other) && paneKeyOf(other) !== paneKey)
                    .map(other => {
                    return {
                        key: paneKeyOf(other),
                        label: tr('vrs.layout.swapWith', {
                            name: getParticipantName(other.participant, other.title)
                        })
                    };
                })
                : [];
            // Fill by default. In a call of two, a picture whose
            // orientation crosses the stage's shows whole by default
            // (Robert, 2026-10-06). The fit button changes one person;
            // the tap holds until the default changes (pictureFit.ts).
            const remoteParticipant = isRemote ? pane.participant : undefined;
            // Robert, 2026-10-16: another call on this person's iPhone
            // (call-management/otherCall.ts) is said in one steady sentence.
            // Robert, 2026-11-02: while the person is on the other call
            // (you are on hold), a lighter second line with slow dots.
            const otherCallStatus = remoteParticipant
                ? parseOtherCallStatus(remoteParticipant.malkaOtherCall) : '';
            const otherCallName = getParticipantName(remoteParticipant, '');
            const otherCall = otherCallText(otherCallStatus, otherCallName);
            const otherCallLine2 = otherCallHint(otherCallStatus);
            const slot = pictureShapes.slots[paneKey];
            const aspect = pictureShapes.aspects[paneKey];
            const fitByDefault = callOfTwo && crossesSlot(aspect, slot);
            const fitted = remoteParticipant
                ? isPictureFitted(fitChoice, remoteParticipant.id, fitByDefault) : false;
            const shaped = fitted ? fitToSlot(slot?.width, slot?.height, aspect) : undefined;
            const shapedFrameStyle = shaped ? {
                flex: '0 0 auto',
                height: shaped.height,
                width: shaped.width
            } : undefined;
            const fitLabelKey = fitted ? 'vrs.layout.fillPicture' : 'vrs.layout.fitPicture';
            // The partner's pane shows the splash until their video has
            // played once; after that (camera turned off mid-call) it is
            // an ordinary joined pane.
            const showEmptyState = (!shouldRenderVideo || !isVideoReady) && (!pane.peer || isVideoReady);
            const markCurrentTrackReady = () => markTrackReady(pane.videoTrack);
            const recordRemoteVideo = (phase) => event => {
                if (pane.participant?.local) {
                    return;
                }
                remoteVideoDiagnostics.observe(paneKey, pane.role, phase, event.currentTarget);
            };
            const onRemoteVideoPlaying = event => {
                markCurrentTrackReady(event);
                recordRemoteVideo('playing')(event);
            };
            const held = pane.participant && !pane.participant.local
                ? getHeldFrame(pane.participant.id) : undefined;
            const showHeld = Boolean(held) && shouldShowHeldFrame({
                heldAt: held?.at,
                now: Date.now()
            });
            // The partner's pane keeps one key from waiting to live, so
            // their video replaces the splash in place.
            return (React.createElement("section", { ...(swappable ? tileDrag.tileProps(paneKey) : {}), className: cx(classes.pane, shaped && classes.paneShaped, isFloatingSelfView && classes.vrsSelfPip, isFloatingSelfView && showEmptyState && classes.vrsSelfPipEmpty, shouldShowDevicePanel && deviceSettingsOpen && classes.paneDeviceSettingsOpen, swappable && classes.paneSwappable, swappable && tileDrag.drag?.key === paneKey && classes.paneLifted, swappable && tileDrag.drag?.target === paneKey && classes.paneDropTarget), "data-desktop-mini": paneKey === desktopMiniKey ? 'video' : undefined, "data-self-view": Boolean(pane.participant?.local), "data-vrs-pane": paneKey, "data-vrs-swappable": swappable || undefined, key: paneKey, ref: isFloatingSelfView ? selfView.tile : undefined, style: isFloatingSelfView ? selfView.style : undefined },
                isFloatingSelfView && (React.createElement("button", { ...selfView.handle, className: classes.moveSelfView, type: 'button' },
                    React.createElement("span", { "aria-hidden": 'true' }, "\u2194"))),
                shouldShowDevicePanel && (React.createElement(SelfViewDeviceSettings, { label: tr('vrs.layout.deviceSettings'), onOpenChange: setDeviceSettingsOpen, open: deviceSettingsOpen, stageRef: selfView.stage },
                    React.createElement("button", { className: classes.deviceAction, disabled: _videoMuteDisabled, onClick: onToggleCamera, type: 'button' }, _videoMuted
                        ? tr('vrs.layout.startCamera')
                        : tr('vrs.layout.stopCamera')),
                    React.createElement("label", { className: classes.deviceRow },
                        React.createElement("span", { className: classes.deviceLabel }, tr('vrs.layout.camera')),
                        React.createElement("select", { className: classes.deviceSelect, onChange: onCameraChange, value: cameraSelectValue },
                            React.createElement("option", { value: '' }, tr('vrs.layout.defaultCamera')),
                            _videoInputDevices.map((device, index) => (React.createElement("option", { key: device.deviceId, value: device.deviceId }, getDeviceLabel(device, tr('vrs.layout.camera'), index)))))),
                    React.createElement("label", { className: classes.deviceRow },
                        React.createElement("span", { className: classes.deviceLabel }, tr('vrs.layout.mic')),
                        React.createElement("select", { className: classes.deviceSelect, onChange: onMicChange, value: micSelectValue },
                            React.createElement("option", { value: '' }, tr('vrs.layout.defaultMic')),
                            _audioInputDevices.map((device, index) => (React.createElement("option", { key: device.deviceId, value: device.deviceId }, getDeviceLabel(device, 'Mic', index)))))))),
                debugOverlays && (React.createElement("div", { className: classes.paneHeader },
                    React.createElement("div", { className: classes.paneTitleBlock },
                        React.createElement("div", { className: classes.paneTitle }, pane.title),
                        React.createElement("div", { className: classes.paneDescription }, pane.description)),
                    React.createElement("div", { className: classes.statusBadge }, pane.statusText))),
                React.createElement("div", { className: classes.mediaFrame, style: shapedFrameStyle },
                    swappable && (React.createElement(TileSwapMenu, { label: tr('vrs.layout.swapPlaces'), onSwap: swapBy('menu'), targets: swapTargets, tileKey: paneKey, visible: _toolboxVisible })),
                    shouldRenderVideo && (React.createElement(React.Fragment, null,
                        React.createElement(VideoTrack, { className: cx(classes.video, isRemote && classes.remoteVideo, !isVideoReady && classes.videoPending, remotePaused && classes.videoPaused), eventHandlers: {
                                onAbort: recordRemoteVideo('abort'),
                                onCanPlay: markCurrentTrackReady,
                                onEmptied: recordRemoteVideo('emptied'),
                                onError: recordRemoteVideo('error'),
                                onLoadedData: markCurrentTrackReady,
                                onLoadedMetadata: recordRemoteVideo('loadedmetadata'),
                                onLoadStart: recordRemoteVideo('loadstart'),
                                onPlaying: onRemoteVideoPlaying,
                                onStalled: recordRemoteVideo('stalled'),
                                onWaiting: recordRemoteVideo('waiting')
                            }, id: pane.role === 'extra' ? `vrs-extra-${pane.participant?.id}-video` : `vrs-${pane.role}-video`, disablePictureInPicture: true, 
                            // A new key re-attaches a stalled remote video to a fresh element.
                            key: isRemote ? `video-${stallGuard.attachKey(paneKey)}` : 'video', muted: pane.participant?.local ? undefined : true, onVideoElement: isRemote ? stallGuard.elementRef(paneKey) : undefined, style: fitted && !shaped ? FIT_VIDEO_STYLE : FILL_VIDEO_STYLE, videoTrack: pane.videoTrack }),
                        remotePaused && (React.createElement("div", { "aria-label": tr('vrs.layout.videoPaused'), className: classes.pausedMark, role: 'img' })),
                        isVideoReady && (React.createElement(React.Fragment, null,
                            React.createElement("div", { className: cx(classes.participantLabel, !namesAlwaysOn && !_toolboxVisible && classes.participantLabelHidden), "data-vrs-name": true, 
                                // The floating self view rises above the bar itself.
                                style: nameLifts[paneKey] && !isFloatingSelfView
                                    ? { transform: `translateY(-${nameLifts[paneKey]}px)` }
                                    : undefined },
                                React.createElement("div", { className: classes.participantName }, participantName),
                                debugOverlays && (React.createElement("div", { className: classes.participantMeta }, pane.description))),
                            remoteParticipant && (React.createElement(PictureFitButton, { fitByDefault: fitByDefault, fitted: fitted, label: tr(fitLabelKey), onToggle: toggleFit, participantId: remoteParticipant.id, visible: _toolboxVisible })))))),
                    showHeld && held && (React.createElement(HeldFrame, { canvas: held.canvas, className: cx(classes.heldFrame, shouldRenderVideo && isVideoReady && classes.heldFrameFading) })),
                    showEmptyState && !showHeld && (React.createElement("div", { className: cx(classes.emptyState, isFloatingSelfView && classes.vrsSelfPipEmptyState) },
                        React.createElement("div", { className: cx(classes.emptyBadge, isFloatingSelfView && classes.vrsSelfPipEmptyBadge) }, pane.title.charAt(0)),
                        React.createElement("div", { className: cx(classes.emptyTitle, isFloatingSelfView && classes.vrsSelfPipEmptyTitle) }, participantName),
                        React.createElement("div", { className: cx(classes.emptyCopy, isFloatingSelfView && classes.vrsSelfPipEmptyCopy) }, emptyMessage))),
                    otherCall && (React.createElement("div", { "aria-live": 'polite', className: classes.otherCallVeil, role: 'status' },
                        React.createElement("span", { className: classes.otherCallSpoken }, otherCallSpoken(otherCallStatus, otherCallName)),
                        React.createElement("span", { "aria-hidden": 'true' }, otherCall),
                        otherCallLine2 && (React.createElement("span", { "aria-hidden": 'true', className: classes.otherCallHint, "data-other-call-hint": 'true' },
                            otherCallLine2,
                            React.createElement("span", { className: classes.otherCallDot }, "."),
                            React.createElement("span", { className: classes.otherCallDot }, "."),
                            React.createElement("span", { className: classes.otherCallDot }, "."))))),
                    pane.peer && (React.createElement(PeerWaitingPane, { peer: pane.peer, peerId: pane.participant?.id, peerName: pane.participant ? getParticipantName(pane.participant, '') : undefined, peerPresent: Boolean(pane.participant), video: getPeerVideo(pane, shouldRenderVideo && isVideoReady) })))));
        }),
        tileDrag.drag && (React.createElement("div", { "aria-hidden": 'true', className: classes.tileGhost, ref: tileDrag.ghostRef, style: {
                height: tileDrag.drag.height,
                width: tileDrag.drag.width
            } },
            React.createElement("span", { className: classes.tileGhostName }, draggedPane ? getParticipantName(draggedPane.participant, draggedPane.title) : ''))),
        _mediaEvidence && debugOverlays && (React.createElement("details", { className: classes.mediaEvidence },
            React.createElement("summary", { className: classes.mediaEvidenceSummary }, `Media health: ${_mediaEvidence.mode}`),
            React.createElement("div", { className: classes.mediaEvidenceGrid },
                React.createElement("span", { className: classes.mediaEvidenceLabel }, "Room"),
                React.createElement("span", { className: classes.mediaEvidenceValue }, `${_mediaEvidence.roomKind.toUpperCase()} - ${_mediaEvidence.participantCount} participants`),
                React.createElement("span", { className: classes.mediaEvidenceLabel }, "Transport"),
                React.createElement("span", { className: classes.mediaEvidenceValue }, `${_mediaEvidence.p2pActive ? 'Direct (P2P)' : 'Bridge (JVB)'} - expected ${_mediaEvidence.expectedTransport === 'p2p' ? 'direct'
                    : _mediaEvidence.expectedTransport === 'jvb' ? 'bridge' : 'bridge (P2P off)'}`),
                React.createElement("span", { className: classes.mediaEvidenceLabel }, "Priority"),
                React.createElement("span", { className: classes.mediaEvidenceValue }, _mediaEvidence.priorityRoles.join(', ') || 'none'),
                React.createElement("span", { className: classes.mediaEvidenceLabel }, "Video"),
                React.createElement("span", { className: classes.mediaEvidenceValue }, `${_mediaEvidence.visibleVideoTrackCount}/${_mediaEvidence.remoteVideoTrackCount} remote tracks visible`),
                React.createElement("span", { className: classes.mediaEvidenceLabel }, "Lower priority"),
                React.createElement("span", { className: classes.mediaEvidenceValue }, _mediaEvidence.hiddenByPolicyCount),
                _mediaEvidence.missingVideoParticipantNames.length > 0 && (React.createElement(React.Fragment, null,
                    React.createElement("span", { className: classes.mediaEvidenceLabel }, "Missing video"),
                    React.createElement("span", { className: classes.mediaEvidenceValue }, _mediaEvidence.missingVideoParticipantNames.join(', ')))))))));
};
function _mapStateToProps(state) {
    const roomRole = state['features/base/jwt']?.user?.role;
    const isObserver = roomRole === 'observer';
    const hasAccountContext = hasPersistentAuthToken() && roomRole !== 'guest' && !isObserver;
    // Observers receive the participant stage without an empty self-camera pane.
    const localParticipant = isObserver ? undefined : getLocalParticipant(state);
    const remoteParticipants = Array.from(state['features/base/participants'].remote.values())
        .filter(isVisibleRoomParticipant);
    const localRole = hasAccountContext ? getStoredVrsRole() : undefined;
    const targetClient = hasAccountContext ? getStoredTargetClient() : undefined;
    const roomName = getRoomName(state);
    const isVriContext = isVriConferenceContext(roomName);
    // A deaf-to-deaf call (p2p-/group-) has no hearing party and no
    // interpreter to wait for: the partner pane (peerWaiting.ts paneRole
    // 'interpreter') holds the first other signer, and everyone else gets a
    // tile of their own — the same rules roomPanes.ts assignRoomPanes applies
    // on native. Without this, a client's peer landed in the hearing pane via
    // firstNonInterpreter() while the partner pane still showed its waiting
    // splash next to the real video.
    const isPlatformRoom = Boolean(roomName?.startsWith('p2p-') || roomName?.startsWith('group-'));
    // Who the local person is waiting for, named from the stored call for
    // this room. Guests and observers get no partner pane. The stored call is
    // read without writing anything back (this runs on every store update).
    const peer = resolvePeerWaiting({
        localRole,
        roomName,
        storedCall: localRole ? readStoredActiveCall() : undefined
    });
    let clientParticipant;
    let interpreterParticipant;
    let hearingParticipant;
    let remainingParticipants = remoteParticipants.slice();
    /*
     * Pane roles bind by the malkaVisualRole each endpoint announces at join
     * (visual-language-media-policy), falling back to the historical
     * positional guess only for peers that never announce (older mobile
     * clients). Guessing order mattered before announcing existed: a guest
     * who joined first stole the interpreter's pane and its blue border.
     */
    const platformRoom = getRoomPaneKind(roomName, false) === 'platform';
    const localVisualRole = hasAccountContext ? getLocalVisualLanguageRole() : undefined;
    const roleByParticipantId = platformRoom
        ? inferPlatformRoomRoles(remoteParticipants)
        : inferRemoteRoles(remoteParticipants, localVisualRole, targetClient);
    const remoteWithRole = (role) => remainingParticipants.find(participant => roleByParticipantId[participant.id] === role);
    // A second interpreter is the call's team interpreter
    // (docs/interpreter-teaming.md): never the hearing party's pane.
    const firstNonInterpreter = () => remainingParticipants.find(participant => roleByParticipantId[participant.id] !== 'interpreter');
    if (isPlatformRoom) {
        // No hearing party, and the first other signer fills the partner
        // pane (the pane a client's peer splash already occupies). A guest in
        // a platform room has no named pane of their own, so the first signer
        // takes the partner pane and the rest stay as tiles.
        if (localRole === 'interpreter') {
            interpreterParticipant = localParticipant;
            clientParticipant = firstNonInterpreter();
            remainingParticipants = withoutParticipant(remainingParticipants, clientParticipant);
        }
        else {
            if (localRole === 'client') {
                clientParticipant = localParticipant;
            }
            interpreterParticipant = firstNonInterpreter();
            remainingParticipants = withoutParticipant(remainingParticipants, interpreterParticipant);
        }
    }
    else if (localRole === 'interpreter') {
        interpreterParticipant = localParticipant;
        clientParticipant = remoteWithRole('client');
        remainingParticipants = withoutParticipant(remainingParticipants, clientParticipant);
        if (!isVriContext) {
            hearingParticipant = firstNonInterpreter();
            remainingParticipants = withoutParticipant(remainingParticipants, hearingParticipant);
        }
    }
    else if (localRole === 'client') {
        clientParticipant = localParticipant;
        interpreterParticipant = remoteWithRole('interpreter');
        remainingParticipants = withoutParticipant(remainingParticipants, interpreterParticipant);
        if (!isVriContext) {
            hearingParticipant = firstNonInterpreter();
            remainingParticipants = withoutParticipant(remainingParticipants, hearingParticipant);
        }
    }
    else {
        hearingParticipant = isVriContext ? undefined : localParticipant;
        interpreterParticipant = remoteWithRole('interpreter');
        remainingParticipants = withoutParticipant(remainingParticipants, interpreterParticipant);
        clientParticipant = remoteWithRole('client');
        remainingParticipants = withoutParticipant(remainingParticipants, clientParticipant);
    }
    // In a deaf-to-deaf room the partner is not "the interpreter" and nobody
    // is "the client", except to an interpreter someone called in (as on
    // native, VRSLayout.native.tsx).
    const clientTitleKey = platformRoom && localRole !== 'interpreter'
        ? 'vrs.layout.extraTitle' : 'vrs.layout.clientTitle';
    const interpreterTitleKey = platformRoom && localRole === 'client'
        ? 'vrs.layout.extraTitle' : 'vrs.layout.interpreterTitle';
    // A phone/PSTN leg arrives as a jigasi participant and the room-wide
    // filter above drops it entirely — leaving deaf clients with zero visual
    // evidence that the hearing party connected, rang, or sat on hold on the
    // one channel they cannot verify by ear. Surface it as the hearing pane.
    if (!hearingParticipant) {
        hearingParticipant = Array.from(state['features/base/participants'].remote.values())
            .find(participant => participant.isJigasi && !participant.fakeParticipant);
    }
    const clientVideoTrack = getVideoTrackByParticipant(state, clientParticipant);
    const interpreterVideoTrack = getVideoTrackByParticipant(state, interpreterParticipant);
    const hearingVideoTrack = getVideoTrackByParticipant(state, hearingParticipant);
    // Relay tenants label the third pane "Hearing Party" because that party is
    // expected on a phone leg. Unified tenants (MalkaVCS) put both parties in
    // the room as equals, so the relay framing would be wrong there.
    const unifiedRoom = isUnifiedRoomModel();
    const thirdPaneKey = unifiedRoom ? 'participant' : 'hearing';
    return {
        _hasAccountContext: hasAccountContext,
        _isObserver: isObserver,
        _localVrsRole: localRole,
        _audioInputDevices: state['features/base/devices'].availableDevices.audioInput || [],
        _currentCameraDeviceId: getCurrentCameraDeviceId(state),
        _currentMicDeviceId: getCurrentMicDeviceId(state),
        _hasLocalVideoTrack: Boolean(getVideoTrackByParticipant(state, localParticipant)),
        _mediaEvidence: getVisualLanguageMediaEvidence(state),
        _roomName: roomName,
        _toolboxVisible: isToolboxVisible(state),
        _tileViewEnabled: shouldDisplayTileView(state),
        _userSelectedCameraDeviceId: getUserSelectedCameraDeviceId(state) || '',
        _videoMuteDisabled: isVideoMuteButtonDisabled(state),
        _videoMuted: isLocalTrackMuted(state['features/base/tracks'], MEDIA_TYPE.VIDEO),
        _videoInputDevices: state['features/base/devices'].availableDevices.videoInput || [],
        _panes: [
            {
                description: tr('vrs.layout.clientDescription'),
                participant: clientParticipant,
                peer: peer?.paneRole === 'client' ? peer : undefined,
                role: 'client',
                statusText: getPaneStatusText(clientParticipant, clientVideoTrack, tr('vrs.layout.awaitingClient')),
                title: tr(clientTitleKey),
                videoTrack: clientVideoTrack
            },
            {
                description: tr('vrs.layout.interpreterDescription'),
                participant: interpreterParticipant,
                peer: peer?.paneRole === 'interpreter' ? peer : undefined,
                role: 'interpreter',
                statusText: getPaneStatusText(interpreterParticipant, interpreterVideoTrack, tr('vrs.layout.awaitingInterpreter')),
                title: tr(interpreterTitleKey),
                videoTrack: interpreterVideoTrack
            },
            {
                description: tr(`vrs.layout.${thirdPaneKey}Description`),
                participant: hearingParticipant,
                role: 'hearing',
                statusText: getPaneStatusText(hearingParticipant, hearingVideoTrack, tr(unifiedRoom ? 'vrs.layout.awaitingParticipant' : 'vrs.layout.awaitingHearing')),
                title: tr(`vrs.layout.${thirdPaneKey}Title`),
                videoTrack: hearingVideoTrack
            },
            // Everyone beyond the three named roles still deserves a video
            // tile — the interpreter especially must be able to see all
            // participants in a group call, not a list of nameplates.
            ...remainingParticipants.map(participant => {
                const videoTrack = getVideoTrackByParticipant(state, participant);
                // A team interpreter is named as an interpreter, as on native (roomPanes.ts).
                const titleKey = roleByParticipantId[participant.id] === 'interpreter'
                    ? 'vrs.layout.interpreterTitle' : 'vrs.layout.extraTitle';
                return {
                    description: tr(titleKey),
                    participant,
                    role: 'extra',
                    statusText: getPaneStatusText(participant, videoTrack, tr('vrs.layout.joining')),
                    title: getParticipantName(participant, tr(titleKey)),
                    videoTrack
                };
            })
        ]
    };
}
export default connect(_mapStateToProps)(VRSLayout);
